import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Eye, MousePointerClick, Check, X, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, naira } from "@/lib/api";

const STATUS_C = {
  pending: "text-[#FFD700]", pending_review: "text-[#FFB300]", approved_unpaid: "text-[#00E5FF]",
  active: "text-[#16A34A]", rejected: "text-[#FF0055]", removed: "text-[#FF0055]", expired: "text-slate-500",
};
const DECISION_C = { approve: "text-[#16A34A]", reject: "text-[#FF0055]", review: "text-[#FFB300]" };

export default function AdminAds() {
  const [ads, setAds] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ads");
      setAds(data.ads);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) setForbidden(true);
      else toast.error(errMsg(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const moderate = async (ad_id, action) => {
    let reason = "";
    if (action !== "approve") reason = window.prompt(`Reason for ${action} (shown to advertiser):`, "") || "";
    setBusy(ad_id + action);
    try {
      await api.post("/admin/ads/moderate", { ad_id, action, reason });
      toast.success(`Ad ${action}d`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (forbidden) {
    return (
      <div className="min-h-[100dvh] bg-[#F8FAFC] flex flex-col items-center justify-center text-center px-6" data-testid="admin-forbidden">
        <Ban className="text-[#FF0055]" size={40} />
        <h1 className="font-display text-2xl mt-3 text-slate-900">Admin only</h1>
        <p className="text-sm text-slate-500 mt-1">Log in with an admin account to moderate ads.</p>
      </div>
    );
  }

  const pendingFirst = ads ? [...ads].sort((a, b) => (a.status === "pending_review" ? -1 : 0) - (b.status === "pending_review" ? -1 : 0)) : [];

  return (
    <div className="min-h-[100dvh] bg-[#F8FAFC] px-5 py-6 max-w-2xl mx-auto" data-testid="admin-ads-page">
      <div className="flex items-center gap-2">
        <ShieldCheck className="text-[#16A34A]" size={24} />
        <h1 className="font-display text-2xl text-slate-900">Ad Moderation</h1>
      </div>
      <p className="text-sm text-slate-500 mt-1">AI pre-screens every ad. Borderline ones wait here as <span className="text-[#FFB300]">under review</span> for your call.</p>

      {ads === null ? (
        <div className="mt-10 flex justify-center text-slate-500"><Loader2 className="animate-spin" /></div>
      ) : ads.length === 0 ? (
        <p className="mt-10 text-center text-slate-400">No ads yet.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {pendingFirst.map((a) => (
            <div key={a.id} data-testid={`admin-ad-${a.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <img src={a.image_url} alt="" className="w-16 h-16 rounded-lg object-cover bg-black shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{a.advertiser_name}</div>
                  <div className="label-xs text-slate-500">{a.slot_name} · {naira((a.price) || 0)}</div>
                  <a href={a.link_url} target="_blank" rel="noopener noreferrer" className="label-xs text-[#00E5FF] break-all">{a.link_url}</a>
                  <div className="label-xs mt-1">Status: <span className={STATUS_C[a.status] || "text-slate-500"}>{a.status}</span></div>
                </div>
                <div className="text-right label-xs text-slate-500">
                  <div className="flex items-center gap-1 justify-end"><Eye size={11} /> {a.impressions}</div>
                  <div className="flex items-center gap-1 justify-end mt-0.5"><MousePointerClick size={11} /> {a.clicks}</div>
                </div>
              </div>

              {a.ai_review && (
                <div className="mt-3 bg-black/40 rounded-lg p-2.5 label-xs">
                  <span className="text-slate-500">AI verdict: </span>
                  <span className={DECISION_C[a.ai_review.decision] || "text-slate-500"}>{a.ai_review.decision}</span>
                  {a.ai_review.category && a.ai_review.category !== "none" && <span className="text-slate-500"> · {a.ai_review.category}</span>}
                  {!a.ai_review.ai_classified && <span className="text-[#FFB300]"> · fallback (AI unavailable)</span>}
                  <div className="text-slate-500 mt-1">{a.ai_review.reason}</div>
                </div>
              )}
              {a.admin_note && <div className="mt-1 label-xs text-slate-500">Admin note: {a.admin_note}</div>}

              <div className="mt-3 flex gap-2">
                {(a.status === "pending_review") && (
                  <button data-testid={`admin-approve-${a.id}`} disabled={!!busy} onClick={() => moderate(a.id, "approve")}
                    className="flex-1 label-xs bg-[#16A34A] text-white py-2 rounded-lg font-bold flex items-center justify-center gap-1 disabled:opacity-50"><Check size={13} /> Approve</button>
                )}
                {(a.status === "pending_review" || a.status === "approved_unpaid" || a.status === "pending") && (
                  <button data-testid={`admin-reject-${a.id}`} disabled={!!busy} onClick={() => moderate(a.id, "reject")}
                    className="flex-1 label-xs bg-[#2a2a2a] text-[#FF0055] border border-[#FF0055]/30 py-2 rounded-lg font-bold flex items-center justify-center gap-1 disabled:opacity-50"><X size={13} /> Reject</button>
                )}
                {a.status === "active" && (
                  <button data-testid={`admin-takedown-${a.id}`} disabled={!!busy} onClick={() => moderate(a.id, "takedown")}
                    className="flex-1 label-xs bg-[#2a2a2a] text-[#FF0055] border border-[#FF0055]/30 py-2 rounded-lg font-bold flex items-center justify-center gap-1 disabled:opacity-50"><Ban size={13} /> Take down</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
